import { Component } from '@angular/core';
import { Router } from '@angular/router';
import { firstValueFrom } from 'rxjs';
import { AuthPageLayoutComponent } from '../../../shared/layout/auth-page-layout/auth-page-layout.component';
import { LabelComponent } from '../../../shared/components/form/label/label.component';
import { InputFieldComponent } from '../../../shared/components/form/input/input-field.component';
import { ButtonComponent } from '../../../shared/components/ui/button/button.component';
import { AuthService } from '../../../shared/services/auth.service';
import { ToastService } from '../../../shared/services/toast.service';

@Component({
  selector: 'app-change-password-required',
  standalone: true,
  imports: [AuthPageLayoutComponent, LabelComponent, InputFieldComponent, ButtonComponent],
  templateUrl: './change-password-required.component.html',
})
export class ChangePasswordRequiredComponent {
  currentPassword = '';
  newPassword = '';
  confirmPassword = '';
  isSubmitting = false;

  get canSubmit(): boolean {
    return !this.isSubmitting;
  }

  constructor(
    private authService: AuthService,
    private toastService: ToastService,
    private router: Router
  ) {}

  async submit(): Promise<void> {
    const user = this.authService.getCurrentUser();
    const identifier =
      this.authService.getPasswordResetIdentifier()?.trim() ||
      (user?.email as string | undefined)?.trim() ||
      (user?.username as string | undefined)?.trim() ||
      '';
    const currentPassword =
      this.currentPassword.trim() || this.authService.getPendingResetPassword() || '';
    if (!identifier) {
      this.toastService.error('Unable to identify your account. Please sign in again.');
      this.authService.logout();
      return;
    }
    if (!currentPassword) {
      this.toastService.error('Current password is required.');
      return;
    }

    this.isSubmitting = true;
    try {
      await firstValueFrom(
        this.authService.changePassword(
          identifier,
          currentPassword,
          this.newPassword,
          this.confirmPassword,
          { suppressSuccessToast: true, suppressErrorToast: true }
        )
      );
      this.authService.clearPasswordResetRequired();
      this.toastService.success('Password changed. Please sign in again.');
      this.authService.logout();
    } catch (error: any) {
      const message = error?.error?.message || error?.message || 'Unable to change password.';
      this.toastService.error(message);
    } finally {
      this.isSubmitting = false;
    }
  }

  cancelToLogin(): void {
    this.authService.logout();
    this.router.navigate(['/signin']);
  }
}
